/**
 * Inline category badge + action affordance for evidence requirements.
 *
 * Reads `control.auto_evidence.category` (the 8-category source of
 * truth shipped Feb 2026) and renders a plain-language badge with a
 * single contextual action button:
 *
 *   • auto_satisfied            → "Auto-satisfied" (no action)
 *   • policy_violation          → "Policy violation" → View violations
 *   • need_more_evidence        → "Need more evidence" → View details
 *   • not_applicable            → "Not applicable" (no action)
 *   • collector_error           → "Collector error" → Retry
 *   • integration_disconnected  → "{GitHub|GW} not connected" → Connect
 *   • collector_unavailable     → "Collector unavailable" → Upload manually
 *   • manual_evidence_required  → "Manual evidence required" → Upload manually
 *
 * Action buttons are NOT wired to backend calls here — instead they
 * dispatch a single `window.dispatchEvent` so the parent ControlDetail
 * (or the audit-level wrapper) can handle them centrally. Keeps this
 * component free of axios / routing dependencies and lets the parent
 * own the "Retry Auto-Evidence" flow.
 */
import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { RefreshCw, Plug, Upload, AlertTriangle } from 'lucide-react';

const CATEGORY_META = {
  auto_satisfied: {
    label: 'Auto-satisfied',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
  policy_violation: {
    label: 'Policy violation',
    className: 'bg-red-100 text-red-800 border-red-300',
  },
  need_more_evidence: {
    label: 'Need more evidence',
    className: 'bg-orange-100 text-orange-800 border-orange-300',
  },
  collector_error: {
    label: 'Collector error',
    className: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  integration_disconnected: {
    label: 'Integration not connected',
    className: 'bg-sky-100 text-sky-800 border-sky-300',
  },
  collector_unavailable: {
    label: 'Collector unavailable',
    className: 'bg-slate-100 text-slate-700 border-slate-300',
  },
  manual_evidence_required: {
    label: 'Manual evidence required',
    className: 'bg-slate-50 text-slate-700 border-slate-300',
  },
  not_applicable: {
    label: 'Not applicable',
    className: 'bg-slate-100 text-slate-600 border-slate-300',
  },
};

const statusToCategory = (status) => ({
  pass: 'auto_satisfied',
  fail: 'policy_violation',
  partial: 'policy_violation',
  insufficient_evidence: 'need_more_evidence',
  not_applicable: 'not_applicable',
}[(status || '').toLowerCase()] || 'need_more_evidence');

const integrationName = (s) => ({
  github: 'GitHub',
  google_workspace: 'Google Workspace',
}[s] || s);

export const CategoryBadgeWithAction = ({ control, autoEv, evidenceLabel, applicable = true }) => {
  if (!autoEv) {
    // Pre-evaluation: tell the user what would happen. Default to
    // "Manual evidence required" until Auto-Evidence is run.
    const meta = CATEGORY_META.manual_evidence_required;
    return (
      <Badge variant="outline" className={`${meta.className} text-xs`} data-testid="cat-pre-eval">
        Awaiting Auto-Evidence run
      </Badge>
    );
  }

  // When the matcher says this specific required item is NOT covered by the
  // control-level signal, show it as manual rather than broadcasting the
  // control's auto verdict (e.g. a Gmail-TLS signal must not "auto-satisfy"
  // a policy-document or network-log item).
  const category = applicable === false
    ? 'manual_evidence_required'
    : (autoEv.category || statusToCategory(autoEv.status));
  const meta = CATEGORY_META[category] || CATEGORY_META.need_more_evidence;
  const sourceName = integrationName(autoEv.source);
  const label =
    category === 'integration_disconnected' && sourceName
      ? `${sourceName} not connected`
      : meta.label;

  const dispatchAction = (action) => () => {
    window.dispatchEvent(new CustomEvent('auto-evidence-action', {
      detail: {
        action,
        category,
        controlId: control.id,
        auditId: control.audit_id,
        source: autoEv.source || null,
        evidenceLabel: evidenceLabel || null,
        reason: autoEv.reason || autoEv.proof || null,
      },
    }));
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`cat-${category}-${control.id}`}>
      <Badge variant="outline" className={`${meta.className} text-xs`}>
        {label}
      </Badge>

      {autoEv.rule_key && category === 'auto_satisfied' && (
        <span className="text-[11px] text-slate-500">
          via <code className="bg-white px-1 rounded">{autoEv.rule_key}</code>
        </span>
      )}

      {category === 'policy_violation' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[11px] text-red-700 hover:bg-red-50 px-2"
          onClick={dispatchAction('view-details')}
          data-testid="action-view-violations"
        >
          <AlertTriangle className="w-3 h-3 mr-1" /> View violations
        </Button>
      )}

      {category === 'need_more_evidence' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[11px] text-orange-700 hover:bg-orange-50 px-2"
          onClick={dispatchAction('view-details')}
          data-testid="action-view-fix"
        >
          View details
        </Button>
      )}

      {category === 'collector_error' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[11px] text-amber-800 hover:bg-amber-100 px-2"
          onClick={dispatchAction('retry')}
          data-testid="action-retry"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Retry
        </Button>
      )}

      {category === 'integration_disconnected' && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[11px] text-sky-800 hover:bg-sky-100 px-2"
          onClick={dispatchAction('connect-integration')}
          data-testid="action-connect"
        >
          <Plug className="w-3 h-3 mr-1" /> Connect {sourceName}
        </Button>
      )}

      {(category === 'collector_unavailable' || category === 'manual_evidence_required') && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-[11px] text-slate-700 hover:bg-slate-100 px-2"
          onClick={dispatchAction('upload-manual')}
          data-testid="action-upload-manual"
        >
          <Upload className="w-3 h-3 mr-1" /> Upload manually
        </Button>
      )}

      {autoEv.reason && (
        <span className="text-[11px] text-slate-500 max-w-[28rem]" title={autoEv.reason}>
          {autoEv.reason.length > 80 ? autoEv.reason.slice(0, 80) + '…' : autoEv.reason}
        </span>
      )}
    </div>
  );
};

export default CategoryBadgeWithAction;
